/**
 * `SiteUsageCard` — ADR-0263 S2 staff view of per-site current-month
 * usage (page loads, orders, assistant requests) alongside the
 * resolved v2 limits and add-on headroom.
 *
 * Mounts on the account detail Plan & billing tab, next to the S1
 * `SitePlansCard`. GET `/internal/admin/accounts/:id/site-usage` on
 * mount; the response carries each site's three meters + plan ref.
 * No writes; the route writes a `staff_audit_log` 'usage_view' row.
 *
 * Support role can read; the route gates min role in
 * `admin/staff-roles.ts`.
 *
 * The staff surface stays visible even while the v2 catalog is draft
 * (plan_source = 'none'), so staff can inspect counts (the meters
 * show as `unlimited`); the merchant-facing surface hides itself
 * (S3). That asymmetry is intentional — ops always wants to see.
 */

import { useEffect, useState } from 'react';

import type {
  AdminAccountSiteUsageResponse,
  AdminAccountSiteUsageRow,
  AdminPlanV2Meter,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { useC } from '../../lib/theme.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface SiteUsageCardProps {
  accountId: string;
}

const DIMENSION_LABEL: Readonly<Record<AdminPlanV2Meter['dimension'], string>> = {
  page_loads_monthly: 'Page loads',
  orders_monthly: 'Orders',
  assistant_requests_monthly: 'Assistant requests',
};

export function SiteUsageCard({ accountId }: SiteUsageCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [data, setData] = useState<AdminAccountSiteUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async (): Promise<void> => {
      try {
        const res = await api.getAccountSiteUsage(accountId);
        if (cancelled) return;
        setData(res);
        setError(null);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        if (!cancelled) setError(`Could not load site usage${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, api]);

  return (
    <Card className="p-5 sm:p-6">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 6,
        }}
      >
        PLANS V2 · USAGE THIS MONTH
      </div>
      <div
        style={{
          fontFamily: serifFont,
          fontSize: 24,
          color: C.text,
          letterSpacing: -0.5,
          lineHeight: 1,
        }}
      >
        Per-site meters
      </div>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.5,
          marginTop: 6,
          marginBottom: 16,
        }}
      >
        ADR-0263 §2.7 soft dimensions. Page loads and orders are per site; AI
        assistant requests roll up per account.
      </p>
      {loading ? (
        <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          Loading usage…
        </p>
      ) : error !== null ? (
        <p style={{ fontFamily: sansFont, fontSize: 13, color: '#FF6B47' }}>
          {error}
        </p>
      ) : data === null || data.sites.length === 0 ? (
        <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
          No live sites.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {data.sites.map((site) => (
            <SiteRow key={site.site_id} site={site} />
          ))}
          {/* ADR §2.3 — one account-level assistant row below the
              per-site rows. Null when the account has no v2 sites. */}
          {data.account_assistant_requests !== null ? (
            <AccountAssistantRow row={data.account_assistant_requests} />
          ) : null}
        </div>
      )}
    </Card>
  );
}

function AccountAssistantRow({
  row,
}: {
  row: AdminPlanV2Meter & {
    readonly anchor_site_id: string;
    readonly contributing_site_ids: readonly string[];
  };
}): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        padding: '12px 14px',
      }}
      data-testid="account-assistant-row"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          ACCOUNT · ASSISTANT REQUESTS
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          {row.contributing_site_ids.length} SITE
          {row.contributing_site_ids.length === 1 ? '' : 'S'} · ANCHOR {row.anchor_site_id}
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <MeterLine meter={row} />
      </div>
    </div>
  );
}

function SiteRow({ site }: { site: AdminAccountSiteUsageRow }): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.text,
          }}
        >
          {site.site_id}
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          {site.plan_name !== null
            ? `${site.plan_name} · ${site.plan_source}`
            : 'NO V2 PLAN'}
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
        <MeterLine meter={site.page_loads} />
        <MeterLine meter={site.orders} />
      </div>
    </div>
  );
}

function MeterLine({ meter }: { meter: AdminPlanV2Meter }): JSX.Element {
  const C = useC();
  const label = DIMENSION_LABEL[meter.dimension];
  const usage_str = meter.usage.toLocaleString('en-US');
  const limit_str = meter.limit === null ? '∞' : meter.limit.toLocaleString('en-US');
  const headroom_str =
    meter.addon_headroom > 0
      ? ` (base ${meter.base_limit?.toLocaleString('en-US') ?? '∞'} + addon ${meter.addon_headroom.toLocaleString('en-US')})`
      : '';
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 10,
        fontFamily: sansFont,
        fontSize: 12,
        color: C.text,
      }}
    >
      <span>
        {label}
        {meter.scope === 'account' ? (
          <span style={{ color: C.muted, marginLeft: 4 }}>(account)</span>
        ) : null}
      </span>
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: stateColor(meter.state, C.muted),
        }}
      >
        {usage_str} / {limit_str}
        {meter.percent !== null ? ` · ${meter.percent}%` : ''}
        {headroom_str}
      </span>
    </div>
  );
}

function stateColor(state: AdminPlanV2Meter['state'], muted: string): string {
  switch (state) {
    case 'over':
      return '#FF6B47';
    case 'near':
      return '#FFB627';
    case 'ok':
      return '#D9FF3F';
    case 'unlimited':
    default:
      return muted;
  }
}
